<template>
  <div v-if="show" class="cm-activity-background">
    <ConfirmCard title="Are you sure?" :text="confirmText" :index="99" :action="confirmBoxAction" />
    <Box :title="name" header-color="#0F74BB" :show-close="true" :small="true" @close="$emit('close')" width="800px" icon="mdi-lock">
        <div class="cm-permissions-container">
          <LoadingBar :loading="isLoading" :spinner="true"/>
          <div class="cm-permission-pad">
            <div class="cm-permission-categories">
              <div v-for="cat in categories" :key="cat.value">
                <div v-if="displayedPermissionsList[cat.value] && displayedPermissionsList[cat.value].length" :class="['cm-category', {active: selectedCategory == cat.value}]" @click="selectedCategory = cat.value">
                  <p>{{cat.text}}</p>
                </div>
              </div>
            </div>
            <div v-for="cat in categories" :key="cat.value">
              <div v-if="selectedCategory == cat.value" class="cm-permission-options">
                <div class="cm-checkbox" v-for="item in displayedPermissionsList[cat.value]" :key="item">
                  <input class="cm-cb" type="checkbox" :value="item" :disabled="!allowedPermissions.includes(item)" v-model="newPermissions" />
                  <label :style="{color: !allowedPermissions.includes(item) ? '#dddddd !important' : 'black'}">{{mapLabel(item)}}</label>
                </div>
              </div>
            </div> 
            <div class="current-changes">
              <h4 v-if="changesToBeCommited.added.length" class="section-title">added</h4>
              <p v-for="add in changesToBeCommited.added" :key="add">{{mapLabel(add)}}</p>

              <h4 v-if="changesToBeCommited.removed.length" class="section-title" :style="{'margin-top': changesToBeCommited.added ? '20px' : 0}">removed</h4>
              <p v-for="removed in changesToBeCommited.removed" :key="removed">{{mapLabel(removed)}}</p>
            </div>
          </div>
        </div>
        <div class="cm-permission-actions">
          <v-btn class="cm-permissions-update" style="margin-right: 10px" :loading="isLoading" :disabled="isLoading" @click="$emit('close')">Cancel</v-btn>
          <v-btn class="cm-permissions-update" :loading="isLoading" :disabled="isLoading || !hasChanges" @click="UpdatePermissionsConfirm">Update</v-btn>
        </div>
    </Box>
  </div>
</template>
  
<script>
import Box from "@/components/general/Box.vue"
import LoadingBar from '@/components/general/Loader.vue'
import ConfirmCard from '@/components/general/ConfirmCard.vue'
import { GetPermissions, UserPermissions, UpdatePermissions } from '@/services/API.js'
import { Logger } from "aws-amplify"
const logger = new Logger("USER PERMISSIONS")

const labelMap = { 
  // groups
	org: 'Organisations',
  user: 'Users',
  geofence: 'Geofencing',
  groups: 'Groups',
  tracking: 'Tracking',
  vehicle: 'Vehicles',
  device: 'Devices',
  firmware: 'Firmware/Device Config',
  alerts: 'Driving Events',
  reports: 'Reports',
  config: 'Email Config',
  websockets: 'Live Data',
  files: 'Files',
  videostreaming: 'Video Streaming',
  contacts: 'Contacts',
  jobs: 'Jobs',
  assets: 'Assets',


  // permissions
  "assets:updateAsset": "Modify asset",
	"assets:disableAsset": "Disable asset",
	"assets:viewAsset": "View asset",
  "assets:deleteAsset": 'Delete asset',
	"assets:moveAsset": "Move asset",
	"assets:batchAssignDevicesToAssets": "Assign device to asset",
	"assets:createAsset": "Create asset",
	"assets:batchUnassignAssetsFromDevices": "Unassign device from asset",
  'org:updateOrg': 'Modify organisation',
  'org:disableOrg': 'Disable organisation',
  'org:listOrgs': 'See organisation list',
  'org:viewOrg': 'View organisation',
  'org:createOrg': 'Create organisation',
  'org:deleteOrg': 'Delete organisation',
  'user:updateUserRole': 'Modify user role',
  'user:updateUserProfile': 'Modify user',
  'user:listUsers': 'View users',
  'user:createUser': 'Create user',
  'user:updateUserStatus': 'Modify user status',
  'user:updateUserPermissions': 'Modify user permissions',
  'user:deleteUser': 'Disable user',
  'groups:moveUserToGroup': 'Move user to group',
  'groups:deleteGroup': 'Delete group',
  'groups:listGroups': 'View groups',
  'groups:moveVehicleToGroup': 'Move vehicle to group',
  'groups:addGroup': 'Add group',
  'groups:modifyGroup': 'Modify group',
  'geofence:editGeofence': 'Modify geofence',
  'geofence:unshareGeofence': 'Unshare geofence',
  'geofence:createGeofence': 'Create geofence',
  'geofence:deleteGeofence': 'Delete geofence',
  'geofence:disableGeofence': 'Disable geofence',
  'geofence:viewGeofence': 'View geofence',
  'geofence:shareGeofence': 'Share geofence',
  'tracking:TrackVehicle': 'Track vehicle',
  'tracking:ViewVehicleRoutes': 'View vehicle routes',
  'vehicle:viewVehicle': 'View vehicle',
  'vehicle:disableVehicle': 'Disable vehicle',
  'vehicle:getVehiclePresence': 'View vehicle status',
  'vehicle:updateVehicle': 'Modify vehicle details',
  'vehicle:createVehicle': 'Create vehicle',
  'vehicle:unassignDeviceFromVehicle': 'Unassign device from vehicle',
  'vehicle:deleteVehicle': 'Delete vehicle',
  'device:deleteDevice': 'Delete device',
  'device:createDevice': 'Create device',
  'device:disableDevice': 'Disable device',
  'device:assignDeviceToVehicle': 'Assign device to vehicle',
  'device:viewDeviceInfo': 'View device information',
  'firmware:deleteFirmwarePackage': 'Delete firmware package',
  'firmware:listFirmwarePackages': 'List firmware packages',
  'firmware:createFirmwarePackage': 'Create firmware package',
  'firmware:listFirmwareRollouts': 'List firmware rollouts',
  'firmware:sendConfigurationToDevice': 'Send configuration to device',
  'firmware:retrieveConfigurationFromDevice': 'Retrieve configuration from device',
  "firmware:modifyFirmwareRollout": 'Modify firmare rollout',
	"firmware:createFirmwareRollout": "Create firmware rollout",
  'alerts:exportAlertMedia': 'Export event media',
  'alerts:viewAlertInfo': 'View driving event media',
  'alerts:rateAlert': 'Rate driving events',
  'alerts:setAlertViewState': 'Mark alerts as read',
  'alerts:listAlerts': 'View driving events',
  'alerts:archiveAlert': 'Archive driving events',
  'reports:createDrivingEventsReport': 'Run driving events report',
  "reports:scheduleReports": 'Schedule reports',
  'reports:createMileageReport': 'Run mileage report',
  'reports:runUserActivityReport': 'Run user activity report',
  'reports:createIgnitionEventsReport': 'Run ignition report',
  'reports:exportReport': 'Export report',
  'reports:createGeofenceReport': 'Run geofence report',
  'reports:createIOEventsReport': 'Run I/O events report',
  'reports:createSpeedingReport': 'Run speeding report',
  'reports:createTripsReport': 'Run trips report',
  'reports:createUserActivityReport': 'Run user activity report',
  'config:viewAlertEmailList': 'View alert email settings',
  'config:editAlertConfig': 'Modify alert settings',
  'config:modifyAlertEmailList': 'Modify alert email settings',
  'config:editAlertUploadSettings': 'Modify alert media upload settings',
  'websockets:getOrgUpdates': 'Receive org updates',
  'websockets:getPresenceUpdates': 'Receive presence updates',
  'websockets:getFileProgressUpdates': 'Receive file progress updates',
  'websockets:getAlertUpdates': 'Receive alert updates',
  'websockets:connect': 'Receive live data',
  'files:listFilesets': 'View retrieved media',
  'files:exportFileset': 'Export retrieved media',
  'files:shareFileset': 'Share retrieved media',
  'files:listDeviceFiles': 'View device files',
  'files:makeFilesetRequest': 'Retrieve remote media',
  'files:exportVideo': 'Download retrieved video',
  'files:deleteFileset': 'Delete retrieved media',
  'videostreaming:WatchVOD': 'Remote video playback',
  'videostreaming:WatchLivestream': 'View live streams',
  'contacts:listExternalContacts': 'View external contacts',
  'contacts:createExternalContact': 'Create external contacts',
  'contacts:deleteExternalContact': 'Delete external contacts',
  'contacts:modifyExternalContact': 'Modify external contacts',
  'jobs:deleteJob': 'Delete job',
  'jobs:createFirmwareUpdateJob': 'Create firmware update job',
  'jobs:createDeviceConfigJob': 'Create device configuration job',
  'jobs:listJobs': 'List jobs',
  'jobs:createDeviceInstallJob': 'Create device install job',
  'jobs:archiveJob': 'Archive job'
}

export default {
  components: {
    Box,
    LoadingBar,
    ConfirmCard
  },

  props: {
    show: {
      type: Boolean,
      default: () => false
    },
    user: {
      type: [Object, Boolean],
      default: () => {}
    },
    users: {
      type: [Array],
      default: () => []
    },
  },

  data () {
    return {
      currentPermissions: [],
      permissionsList: {},
      selectedCategory: 'user',
      loading: 0,
      newPermissions: [],
      confirmBoxAction: () => {}
    }
  },

  computed: {
    isLoading () {
      return this.loading > 0
    },

    // the name of the user we are editing
    name () {
      return this.userGroupList.length > 1
        ? `${this.userGroupList.length} users`
        : this.user && this.user.Profile 
          ? this.user.Profile.firstname + ' ' + this.user.Profile.lastname
          : 'User activity'
    },

     // text shown in confirm box before changes are commited
    confirmText () {
      let text = `These permissions will be applied to ${this.usersWithSameRole.length || 1} user${this.usersWithSameRole.length > 1 ? 's' : ''}.`
      let excluded = this.users.length - this.usersWithSameRole.length

      if(excluded > 0) {
        text += ` ${excluded} users were excluded because they had a different role.`
      }

      text += ' Are you sure you want to proceed?'

      return text
    },

    // the list of permissions the user can set. here we remove a few we want to hide, and ensure there are no duplicates
    displayedPermissionsList () {
      let list = {}

      Object.keys(this.permissionsList).forEach(cat => {
        if(!this.hiddenCategories.includes(cat)) {
          list[cat] = [... new Set(this.permissionsList[cat])].filter(i => !this.hiddenPermissions.includes(i))
        }
      })

      return list
    },

    hiddenCategories () {
      return ['websockets']
    },

    hiddenPermissions () {
      const perms = [
        'device:createDevice',
        'files:listDeviceFiles',
        'files:exportVideo',
        'alerts:setAlertViewState',
        'geofence:unshareGeofence',
        'vehicle:disableVehicle',
        'device:disableDevice'
      ]

      return perms
    },

    // the list of categories we can show to the user on the side menu
    categories () {
      return this.permissionsList 
        ? Object.keys(this.permissionsList).map(i => ({
          text: this.mapCategory(i),
          value: i
        }))
        : []
    },

    // title text showing how many users
    usersText () {
      let string = `${this.usersWithSameRole.length} users selected`
      let excluded = this.users.length - this.usersWithSameRole.length

      if(excluded > 0) {
        string += `, ${excluded} users had different roles`
      }
      return string
    },

    userNameText () {
      return this.user && this.user.Profile 
          ? this.user.Profile.firstname + ' ' + this.user.Profile.lastname
          : 'User activity'
    },


    // a list of users we send to the api to update
    userGroupList () {
      let set = this.usersWithSameRole
      if(!set.includes(this.user.id)) {
        set.push(this.user.id)
      }
      return set
    },

    selectedUserRole () {
      return this.user.Role.name
    },

    usersWithSameRole () {
      return this.users.filter(i => i.Role.name == this.selectedUserRole).map(i => i.id)
    },

    // returns an array of user ids 
    userList () {
      return [this.user.id]
    },
    
    changesToBeCommited () {
      return {
        added: this.newPermissions.filter(i => !this.currentPermissions.includes(i)),
        removed: this.currentPermissions.filter(i => !this.newPermissions.includes(i))
      }
    },

    // true if something has changed 
    hasChanges () {
      return this.changesToBeCommited.added.length || this.changesToBeCommited.removed.length 
    }
  },

  watch: {
    show (val) {
      if(val) {
        this.fetchPermissions()
      }
    }
  },


  mounted () {
    this.fetchPermissions()
  },

  methods: {
    // returns a full list of all permisions available
     fetchPermissions () {
      this.loading++
      this.loading++
      GetPermissions().then(res => {
        this.permissionsList = res.actions
        this.loading--
        logger.debug("permissions fetched", res)
      }).catch(err => {
        this.loading--
        logger.debug("permissions error", err) 
      })

      if(!this.userList || !this.userList.length || !this.userList[0]) {
        this.loading--
        return    
      }
      
      // fetches a users permission document
      UserPermissions({users: this.userList}).then(res => {
        // the user permissions doc details permissions the user DOES NOT have
        // to get the current permissions a user does have, we go through the allowed permissions document, and filter out any 
        // permissions that feature in the user permissions doc
        let curPermissions = res.users[0].user_permission_document
        this.allowedPermissions = res.users[0].role_permission_document
        this.currentPermissions = this.allowedPermissions.filter(p => !curPermissions.includes(p))
        this.newPermissions = JSON.parse(JSON.stringify(this.currentPermissions))
        this.loading--
        logger.debug("user info fetched", res)
      }).catch(err => {
        this.loading--
        logger.debug("user info error", err) 
      })
    },

    UpdatePermissionsConfirm () {
      this.confirmBoxAction = () => this.UpdatePermissions()
    },

    UpdatePermissions () {
      // can't submit anything if we are loading already
      if(this.isLoading) {
        return
      }

      // build an array of permissions we need to send in the update request
      // this list is currently the permissions the user has, and we need to send to the API a list of permissions 
      // the user DOES NOT have. again, we need to send the inverse of what we have here
      let newPermissionsDoc = this.allowedPermissions.filter(i => !this.newPermissions.includes(i))

      this.loading++

      UpdatePermissions({
        users: this.userGroupList.length ? this.userGroupList : this.userList,
        actions: newPermissionsDoc
      }).then(res => {
        this.currentPermissions = this.newPermissions
        this.newPermissions = JSON.parse(JSON.stringify(this.currentPermissions))
        this.loading--
        this.$toasted.show("Permissions updated!")
        logger.debug("update req suc", res)
      }).catch(err => {
        this.$toasted.show("Something went wrong")
        this.loading--
        logger.debug("update req err", err)
      })
    },

    // map category names to user friendly versions
    mapCategory (category) {
      return labelMap[category] || category
    },

    mapLabel (label) {
      return labelMap[label] || label
      /*
      if(label.includes(':')) {
        label = (label.split(':'))[1]
      }
      label = label.replace(/([A-Z])/g, " $1");
      label = label.charAt(0).toUpperCase() + label.slice(1);
      return label.replace(/_/g, ' ')
      */
    },

    submit () {   
      logger.debug("updating permissions") 
    }, 
  }
}
</script>

<style lang="scss">
// list of changes that have been made so far
.current-changes {
  width: 250px; 
  padding: 0 5px 5px 5px; 
  margin-left: 20px;

  .section-title {
    font-size: 12px; 
    font-weight: light;
    letter-spacing: 0.08em;
    color: #aaa;
    text-transform: uppercase;
    margin: 0 !important;
    padding: 0;
  }

  p {
    font-size: 10px !important;
    font-weight: light !important;
    letter-spacing: 0.07em !important;
    color: #aaa !important;
    margin: 0 !important;
    padding: 0 !important;
  }
}

// container for the buttons
.cm-permission-actions {
  float: right;
  padding-bottom: 15px;
  padding-right: 15px;
}

.cm-permissions-container {
  min-height: 300px;
  position: relative;

  .cm-permission-pad {
    padding: 10px 20px;
    display: flex;
  }

  .cm-permission-options {
    margin-left: 20px;
    padding: 10px 20px;
    background: #eee;
    width: 300px !important;
  }

  .cm-permission-categories {
    .cm-category {
      padding: 5px 10px;
      border: solid 1px #ccc;
      border-radius: 5px;
      display: flex;
      align-items: center;
      justify-content: space-around;
      margin-bottom: 4px;
      width: 180px !important;
      
      &:hover {
        cursor: pointer;
      }

      p {
        font-weight: bold;
        margin: 0;
        padding: 0;
      }

      &.active {
        background: #e7f1fd;
      }
    }
  }

  .cm-checkbox {
    display: flex;
    align-items: center;

    label {
      color: black !important;
      padding-left: 10px;
      font-size: 12px !important;
      letter-spacing: 0.07em;
    }

    // checkbox style
    input[type=checkbox] {
      background: white;
      color: black;
      accent-color:  white;
      height: 16px;
      width: 16px;
    }
  }
}
</style>